import { Box, Checkbox, Dialog, DialogActions, DialogContent, IconButton, Typography, useTheme } from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';
import { IoClose } from 'react-icons/io5';
import { MdBusiness } from 'react-icons/md';
import Button from '@/components/Common/Button';
import DataTable from '@/components/Common/DataTable';
import { BusinessVoiceEngine, BusinessVoiceEngineStatus, VoiceEngine } from '@/apis/agency/voiceEngines/types';

export interface VoiceEngineUpdateBusinessesModalProps {
  open: boolean;
  onClose: () => void;
  engine: VoiceEngine;
  businesses: BusinessVoiceEngine[];
  allEngines: VoiceEngine[];
  selectedIds: Set<number>;
  onToggle: (businessId: number, eligible: boolean) => void;
  onToggleAllEligible: () => void;
  onConfirm: () => void;
  isUpdating: boolean;
  targetVersionLabel: number | string;
  allEligibleSelected: boolean;
  someEligibleSelected: boolean;
  title?: string;
  subtitle?: string;
}

function engineLabelForBusiness(b: BusinessVoiceEngine, engines: VoiceEngine[]): string {
  const match = engines.find((e) => e.id === b.voice_engine_id);
  if (!match) return '—';
  const v = match.configuration?.version;
  return `${match.configuration.friendly_name} v${v}`;
}

function statusPill(status: BusinessVoiceEngineStatus) {
  const enabled = status === 'enabled';
  return (
    <Box
      component='span'
      sx={{
        display: 'inline-block',
        px: 1.25,
        py: 0.35,
        borderRadius: 10,
        fontSize: 12,
        fontWeight: 400,
        bgcolor: enabled ? '#DBFCDE' : '#FFF2F2',
        color: enabled ? '#077F13' : '#DA0808'
      }}
    >
      {enabled ? 'Enabled' : 'Disabled'}
    </Box>
  );
}

export default function VoiceEngineUpdateBusinessesModal({
  open,
  onClose,
  engine,
  businesses,
  allEngines,
  selectedIds,
  onToggle,
  onToggleAllEligible,
  onConfirm,
  isUpdating,
  targetVersionLabel,
  allEligibleSelected,
  someEligibleSelected,
  title = 'Update All Businesses?',
  subtitle
}: VoiceEngineUpdateBusinessesModalProps) {
  const theme = useTheme();
  const friendly = engine.configuration?.friendly_name ?? 'Voice engine';
  const defaultSubtitle = `Do you want to apply version ${targetVersionLabel} update to the selected businesses in your agency?`;
  const eligibleIds = businesses.filter((b) => b.voice_engine_id === engine.id).map((b) => b.business_id);
  const selectedEligibleCount = eligibleIds.filter((id) => selectedIds.has(id)).length;
  const eligibleCount = eligibleIds.length;
  const selectedCount = businesses.filter((b) => selectedIds.has(b.business_id)).length;
  const totalRows = businesses.length;
  const isAllSelected = allEligibleSelected && eligibleCount > 0;
  const tableRows = businesses.map((b) => {
    const eligible = b.voice_engine_id === engine.id;
    const checked = selectedIds.has(b.business_id);
    const targetLabel = `${friendly} v${targetVersionLabel}`;

    return {
      id: b.business_id,
      name: b.business_name,
      engineLabel: eligible ? targetLabel : engineLabelForBusiness(b, allEngines),
      status: b.status,
      eligible,
      checked
    };
  });

  const columns: ColumnDef<(typeof tableRows)[number]>[] = [
    {
      id: 'selection',
      size: 56,
      header: () => (
        <Checkbox
          indeterminate={someEligibleSelected && !allEligibleSelected}
          checked={eligibleCount > 0 && allEligibleSelected}
          disabled={eligibleCount === 0}
          onChange={onToggleAllEligible}
          color='primary'
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.original.checked}
          disabled={!row.original.eligible}
          onChange={() => onToggle(row.original.id, row.original.eligible)}
          color='primary'
        />
      )
    },
    {
      accessorKey: 'name',
      header: 'Name',
      size: 200,
      cell: ({ row }) => <Typography fontSize={14}>{row.original.name}</Typography>
    },
    {
      accessorKey: 'engineLabel',
      header: 'Engine',
      size: 200,
      cell: ({ row }) => (
        <Typography fontSize={14} color={row.original.eligible ? 'text.primary' : 'text.secondary'}>
          {row.original.engineLabel}
        </Typography>
      )
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 120,
      cell: ({ row }) => statusPill(row.original.status)
    }
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: '650px',
          maxWidth: '100%',
          // height: '80vh',
          borderRadius: 2,
          p: { xs: 1, sm: 1.5 },
          position: 'relative'
        }
      }}
    >
      <IconButton
        aria-label='Close'
        onClick={onClose}
        size='small'
        sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary', zIndex: 1 }}
      >
        <IoClose size={22} />
      </IconButton>

      <DialogContent
        sx={{
          pt: 3,
          px: { xs: 1.5, sm: 2.5 },
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          overflow: 'hidden'
        }}
      >
        <Box display='flex' flexDirection='column' alignItems='center' textAlign='center' mb={2.5}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: theme.palette.mode === 'dark' ? 'action.hover' : '#E3F2FD',
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1.5
            }}
          >
            <MdBusiness size={26} />
          </Box>
          <Typography fontWeight={700} fontSize={20} mb={0.5}>
            {title}
          </Typography>
          <Typography variant='body2' color='text.secondary' maxWidth={480} fontSize={14}>
            {subtitle ?? defaultSubtitle}
          </Typography>
        </Box>

        <DataTable
          data={tableRows}
          columns={columns}
          containerStyles={{
            maxHeight: 460,
            overflowY: 'auto',
            borderColor: theme.palette.divider,
            '& .MuiTableCell-root': {
              padding: '6px 8px'
            }
          }}
        />

        <Typography variant='body2' color='text.secondary' mt={1.5} fontSize={13}>
          {selectedCount} of {totalRows} businesses selected
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, justifyContent: 'flex-end', gap: 1 }}>
        <Button
          variant='outlined'
          color='inherit'
          onClick={onClose}
          disabled={isUpdating}
          sx={{ textTransform: 'none' }}
        >
          No
        </Button>
        <Button
          variant='contained'
          color='primary'
          onClick={() => onConfirm()}
          isPending={isUpdating}
          disabled={selectedEligibleCount === 0}
          sx={{ textTransform: 'none' }}
        >
          {isAllSelected ? 'Yes, Update All' : 'Yes, Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
